'use strict';

const {
    TrackingClient,
    TrackingTransport,
    WebTrackingSessionStorage,
} = require('./client');

const CONFIG_ID = 'buyer-tracking-config';
const CONSENT_EVENT = 'shopchill:consent-changed';
const BOOTSTRAP_MARKER = Symbol.for('shopchill.buyer-tracking.bootstrap');
const PAGE_VIEW_MARKER = Symbol.for('shopchill.buyer-tracking.page-view');
const RUNTIME_PROPERTY = 'ShopChillBuyerTrackingRuntime';
const CONFIG_KEYS = new Set([
    'enabled',
    'session_enabled',
    'page_view_enabled',
    'view_category_enabled',
    'view_content_enabled',
    'search_enabled',
    'select_coupon_enabled',
    'track_order_enabled',
    'debug_enabled',
    'endpoint',
    'schema_version',
    'consent_status',
    'environment',
    'page_type',
    'page_key',
]);

function parseRuntimeConfig(documentObject) {
    const element = documentObject && documentObject.getElementById(CONFIG_ID);
    if (!element || typeof element.textContent !== 'string') {
        return null;
    }

    try {
        return JSON.parse(element.textContent);
    } catch (error) {
        return null;
    }
}

function validateRuntimeConfig(value, windowObject) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const keys = Object.keys(value);
    if (
        keys.length !== CONFIG_KEYS.size
        || keys.some((key) => !CONFIG_KEYS.has(key))
    ) {
        return null;
    }

    const booleanKeys = [
        'enabled',
        'session_enabled',
        'page_view_enabled',
        'view_category_enabled',
        'view_content_enabled',
        'search_enabled',
        'select_coupon_enabled',
        'track_order_enabled',
        'debug_enabled',
    ];
    if (booleanKeys.some((key) => typeof value[key] !== 'boolean')) {
        return null;
    }

    if (
        value.schema_version !== '1.0'
        || !['granted', 'denied'].includes(value.consent_status)
        || typeof value.environment !== 'string'
        || typeof value.page_type !== 'string'
        || value.page_type.length < 1
        || value.page_type.length > 64
        || typeof value.page_key !== 'string'
        || value.page_key.length < 1
        || value.page_key.length > 190
        || typeof value.endpoint !== 'string'
    ) {
        return null;
    }

    try {
        const endpoint = new URL(value.endpoint, windowObject.location.origin);
        if (endpoint.origin !== windowObject.location.origin) {
            return null;
        }
        value.endpoint = endpoint.pathname + endpoint.search;
    } catch (error) {
        return null;
    }

    return Object.freeze({ ...value });
}

function safeDiagnostic(config, windowObject, status) {
    if (
        config
        && config.debug_enabled
        && windowObject.console
        && typeof windowObject.console.debug === 'function'
    ) {
        windowObject.console.debug('[buyer-tracking]', { status });
    }
}

function installBootstrap({
    windowObject = window,
    documentObject = document,
    localStorageObject,
    fetchImplementation,
    dependencies = {},
} = {}) {
    if (!documentObject || documentObject[BOOTSTRAP_MARKER]) {
        return { status: 'already_bootstrapped' };
    }

    Object.defineProperty(documentObject, BOOTSTRAP_MARKER, {
        value: true,
        configurable: false,
        enumerable: false,
        writable: false,
    });

    const parsed = parseRuntimeConfig(documentObject);
    const config = validateRuntimeConfig(parsed, windowObject);
    if (!config) {
        return { status: 'invalid_config' };
    }

    const eventFlags = {
        PageView: config.page_view_enabled,
        ViewCategory: config.view_category_enabled,
        ViewContent: config.view_content_enabled,
        Search: config.search_enabled,
        SelectCoupon: config.select_coupon_enabled,
        TrackOrder: config.track_order_enabled,
    };
    if (
        !config.enabled
        || !config.session_enabled
        || !Object.values(eventFlags).some(Boolean)
    ) {
        return { status: 'disabled' };
    }

    let started = false;
    let client = null;
    const pendingCalls = [];
    const invoke = (eventType, method, input) => {
        if (!eventFlags[eventType]) {
            return Promise.resolve({ ok: true, status: 'client_disabled' });
        }
        if (client) {
            return client[method](input);
        }
        if (config.consent_status !== 'granted') {
            return Promise.resolve({ ok: true, status: 'consent_denied' });
        }

        return new Promise((resolve) => {
            pendingCalls.push({ input, method, resolve });
        });
    };
    const runtime = Object.freeze({
        trackViewCategory: (input) => invoke(
            'ViewCategory',
            'trackViewCategory',
            input
        ),
        trackViewContent: (input) => invoke(
            'ViewContent',
            'trackViewContent',
            input
        ),
        trackSearch: (input) => invoke('Search', 'trackSearch', input),
        trackSelectCoupon: (input) => invoke('SelectCoupon', 'trackSelectCoupon', input),
        trackOrder: (input) => invoke('TrackOrder', 'trackOrder', input),
    });
    windowObject[RUNTIME_PROPERTY] = runtime;

    const startTracking = () => {
        if (started) {
            return;
        }
        started = true;

        try {
            let storage = localStorageObject;
            if (storage === undefined) {
                try {
                    storage = windowObject.localStorage;
                } catch (error) {
                    storage = undefined;
                }
            }
            const fetcher = fetchImplementation
                || (
                    typeof windowObject.fetch === 'function'
                        ? windowObject.fetch.bind(windowObject)
                        : undefined
                );
            const StorageClass = dependencies.WebTrackingSessionStorage
                || WebTrackingSessionStorage;
            const TransportClass = dependencies.TrackingTransport
                || TrackingTransport;
            const ClientClass = dependencies.TrackingClient || TrackingClient;
            const sessions = new StorageClass(storage);
            const anonymous =
                new StorageClass(
                    storage,
                    undefined,
                    'shopchill.anonymous_uuid.v1'
                );
            const transport = new TransportClass({
                endpoint: config.endpoint,
                fetchImplementation: fetcher,
                
                sessionStorage: sessions,
                anonymousStorage: anonymous,

                timeoutMs: 2500,
                maxRetries: 1,

                headersProvider: () => {
                    const csrfToken = documentObject
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute('content');

                    return csrfToken
                        ? {
                            'X-CSRF-TOKEN': csrfToken,
                            'X-Requested-With': 'XMLHttpRequest',
                        }
                        : {};
                },
            });
            client = new ClientClass({
                transport,
                flags: {
                    enabled: config.enabled,
                    sessionPersistence: config.session_enabled,
                    ...eventFlags,
                    debug: config.debug_enabled,
                },
                source: 'web',
                context: {
                    platform: 'web',
                },
                debug: (diagnostic) => safeDiagnostic(
                    config,
                    windowObject,
                    diagnostic.status
                ),
            });

            pendingCalls.splice(0).forEach(({ input, method, resolve }) => {
                Promise.resolve(client[method](input)).then(resolve);
            });

            if (
                config.page_view_enabled
                && !documentObject[PAGE_VIEW_MARKER]
            ) {
                Object.defineProperty(documentObject, PAGE_VIEW_MARKER, {
                    value: true,
                    configurable: false,
                    enumerable: false,
                    writable: false,
                });
                const occurrenceKey = [
                    config.page_type,
                    windowObject.location.pathname,
                    config.page_key,
                ].join(':');

                Promise.resolve(client.trackPageView({
                    pageType: config.page_type,
                    title: documentObject.title || undefined,
                    navigationId: occurrenceKey,
                    pageUrl: windowObject.location.href,
                    referrerUrl: documentObject.referrer || undefined,
                })).catch(() => {
                    safeDiagnostic(config, windowObject, 'page_view_failed');
                });
            }
        } catch (error) {
            pendingCalls.splice(0).forEach(({ resolve }) => {
                resolve({ ok: false, status: 'bootstrap_failed' });
            });
            safeDiagnostic(config, windowObject, 'bootstrap_failed');
        }
    };

    const onReady = () => {
        if (config.consent_status === 'granted') {
            startTracking();
            return;
        }

        windowObject.addEventListener(CONSENT_EVENT, (event) => {
            if (event && event.detail && event.detail.status === 'granted') {
                startTracking();
            }
        }, { once: true });
    };

    if (documentObject.readyState === 'loading') {
        documentObject.addEventListener('DOMContentLoaded', onReady, { once: true });
    } else {
        Promise.resolve().then(onReady);
    }

    return { status: 'installed' };
}

module.exports = {
    BOOTSTRAP_MARKER,
    CONFIG_ID,
    CONFIG_KEYS,
    CONSENT_EVENT,
    PAGE_VIEW_MARKER,
    RUNTIME_PROPERTY,
    installBootstrap,
    parseRuntimeConfig,
    validateRuntimeConfig,
};
